import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ProductCard from '../components/ProductCard';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRange, setSelectedRange] = useState('all');
  const [selectedPrice, setSelectedPrice] = useState('all');
  const [sortBy, setSortBy] = useState('default');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    filterAndSortProducts();
  }, [products, searchTerm, selectedRange, selectedPrice, sortBy]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get('https://janvaahan.com/backend/public/api/products');
      setProducts(response.data);
      setFilteredProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortProducts = () => {
    let filtered = [...products];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Range filter
    if (selectedRange !== 'all') {
      const [min, max] = selectedRange.split('-').map(Number);
      filtered = filtered.filter(product => {
        const range = product.range || 0;
        if (max) {
          return range >= min && range <= max;
        }
        return range >= min;
      });
    }

    // Price filter
    if (selectedPrice !== 'all') {
      const [min, max] = selectedPrice.split('-').map(v => v === 'plus' ? Infinity : parseInt(v.replace(/[^0-9]/g, '')));
      filtered = filtered.filter(product => {
        const price = parseInt(product.price.replace(/[^0-9]/g, ''));
        if (max === Infinity) {
          return price >= min;
        }
        return price >= min && price <= max;
      });
    }

    // Sorting
    if (sortBy === 'price-low') {
      filtered.sort((a, b) => parseInt(a.price.replace(/[^0-9]/g, '')) - parseInt(b.price.replace(/[^0-9]/g, '')));
    } else if (sortBy === 'price-high') {
      filtered.sort((a, b) => parseInt(b.price.replace(/[^0-9]/g, '')) - parseInt(a.price.replace(/[^0-9]/g, '')));
    } else if (sortBy === 'range-high') {
      filtered.sort((a, b) => (b.range || 0) - (a.range || 0));
    } else if (sortBy === 'name-asc') {
      filtered.sort((a, b) => a.name.localeCompare(b.name));
    }

    setFilteredProducts(filtered);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedRange('all');
    setSelectedPrice('all');
    setSortBy('default');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading electric bikes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-black to-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Our Electric Bikes</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Discover the future of mobility with our range of high-performance electric bikes.
            Zero emissions, maximum excitement.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Search and Filter Bar */}
      
        {/* Results Count */}
        <div className="mb-6 flex justify-between items-center">
          <p className="text-gray-600">
            Showing <span className="font-bold text-red-600">{filteredProducts.length}</span> of{' '}
            <span className="font-bold">{products.length}</span> electric bikes
          </p>
          {filteredProducts.length === 0 && (
            <button
              onClick={clearFilters}
              className="text-red-600 hover:text-red-700 font-semibold"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Products Grid */}
        {filteredProducts.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg shadow-md">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">No bikes found</h3>
            <p className="text-gray-600 mb-6">
              We couldn't find any electric bikes matching your criteria.
            </p>
            <button
              onClick={clearFilters}
              className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition"
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProducts.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}

        {/* Featured Section - Only show when no filters applied */}
        {searchTerm === '' && selectedRange === 'all' && selectedPrice === 'all' && sortBy === 'default' && (
          <div className="mt-16">
            <div className="bg-gradient-to-r from-red-600 to-red-800 rounded-2xl p-8 text-white">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="text-3xl font-bold mb-4">Not sure which bike to choose?</h2>
                  <p className="mb-6">
                    Take a test ride and experience the difference yourself. Our experts will help you find the perfect electric bike for your needs.
                  </p>
                  <div className="flex space-x-4">
                    <a
                      href="/test-ride"
                      className="bg-white text-red-600 px-6 py-2 rounded-lg font-semibold hover:bg-gray-100 transition"
                    >
                      Book Test Ride
                    </a>
                    <a
                      href="/contact"
                      className="border-2 border-white text-white px-6 py-2 rounded-lg font-semibold hover:bg-white hover:text-red-600 transition"
                    >
                      Talk to Expert
                    </a>
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-6xl mb-2">🚀</div>
                  <p className="text-lg">Find your perfect ride today!</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Products;